/**
 * ShareLinksList — active share links with copy / open / revoke.
 */

import { useState } from "react"
import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import type { MagicLink } from "@/lib/types/share"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Copy01Icon,
  Delete01Icon,
  LinkSquare02Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons"

interface ShareLinksListProps {
  magicLinks: MagicLink[]
  onCopy: (link: string) => Promise<boolean>
  onDelete: (magicLinkId: string) => void
  isDeleting: boolean
}

export function ShareLinksList({
  magicLinks,
  onCopy,
  onDelete,
  isDeleting,
}: ShareLinksListProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const isExpired = (expiryDate: string, expired?: boolean) => {
    if (expired) return true
    return new Date(expiryDate) < new Date()
  }

  const formatExpiry = (expiryDate: string, forever: boolean) => {
    if (forever) return "Never expires"
    const date = new Date(expiryDate)
    if (isExpired(expiryDate)) {
      return `Expired ${format(date, "MMM d, yyyy")}`
    }
    return `Expires ${format(date, "MMM d, yyyy")}`
  }

  const handleCopy = async (link: string, id: string) => {
    const copied = await onCopy(link)
    if (!copied) return
    setCopiedId(id)
    window.setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <TooltipProvider>
      <ul className="space-y-1.5">
        {magicLinks.map((link) => {
          const expired = isExpired(link.expiryDate, link.expired)
          const justCopied = copiedId === link.magicLinkId

          return (
            <li
              key={link.magicLinkId}
              className={cn(
                "flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border/30",
                expired ? "bg-muted/5 opacity-70" : "bg-muted/10"
              )}
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2 min-w-0">
                  {!expired && (
                    <span
                      className="size-1.5 rounded-full bg-live shrink-0"
                      aria-hidden
                    />
                  )}
                  <p className="text-[12px] font-mono text-muted-foreground truncate">
                    {link.link}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                  {link.domain && (
                    <span className="text-foreground/80 font-medium">
                      {link.domain}
                    </span>
                  )}
                  {link.domain && <span className="text-border">·</span>}
                  <span
                    className={cn(
                      expired && "text-warning dark:text-warning"
                    )}
                  >
                    {formatExpiry(link.expiryDate, link.forever)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-0.5 shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => handleCopy(link.link, link.magicLinkId)}
                      disabled={expired}
                      aria-label="Copy link"
                    >
                      {justCopied ? (
                        <HugeiconsIcon
                          icon={Tick01Icon}
                          size={14}
                          className="text-success dark:text-success"
                        />
                      ) : (
                        <HugeiconsIcon icon={Copy01Icon} size={14} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    {expired ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground"
                        disabled
                        aria-label="Open in new tab"
                      >
                        <HugeiconsIcon icon={LinkSquare02Icon} size={14} />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        asChild
                      >
                        <a
                          href={link.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open in new tab"
                        >
                          <HugeiconsIcon icon={LinkSquare02Icon} size={14} />
                        </a>
                      </Button>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>Open</TooltipContent>
                </Tooltip>

                <AlertDialog>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          disabled={isDeleting}
                          aria-label="Revoke link"
                        >
                          <HugeiconsIcon icon={Delete01Icon} size={14} />
                        </Button>
                      </AlertDialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Revoke</TooltipContent>
                  </Tooltip>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-base font-medium">
                        Revoke share link?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-[13px]">
                        Anyone with this link will immediately lose access.
                        This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete(link.magicLinkId)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Revoke link
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </li>
          )
        })}
      </ul>
    </TooltipProvider>
  )
}
